import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../hooks/useNotification';

interface HeaderProps {
  userName: string;
  onNameChange?: (name: string) => void;
  intimacyLevel?: number;
  intimacyPoints?: number;
  messagesUsedToday?: number;
  messagesLimitPerDay?: number;
  isTyping?: boolean;
}

const LEVEL_LABELS: Record<number, string> = {
  1: 'はじめまして',
  2: 'ちょっと仲良し',
  3: '気になる存在',
  4: '特別な人',
  5: '大切な人',
};

const LEVEL_EMOJIS: Record<number, string> = {
  1: '🌱',
  2: '🌸',
  3: '💛',
  4: '🧡',
  5: '❤️',
};

export default function Header({
  userName,
  onNameChange,
  intimacyLevel = 1,
  intimacyPoints,
  messagesUsedToday = 0,
  messagesLimitPerDay = 30,
  isTyping = false,
}: HeaderProps) {
  const navigate = useNavigate();
  const { getUnreadCount } = useNotification();
  const unreadCount = getUnreadCount();

  const levelLabel = LEVEL_LABELS[intimacyLevel] ?? 'はじめまして';
  const levelEmoji = LEVEL_EMOJIS[intimacyLevel] ?? '🌱';

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-white/30 px-2 py-0.5 flex items-center justify-between shadow-sm gap-1">
      {/* 左：アバター＋名前＋ステータス */}
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <div className="relative w-8 h-8 flex-shrink-0">
          <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/50 shadow-sm bg-pink-50/30">
            <img
              src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/a24adec9501d50c6a1b55366ad633d8e.png"
              alt="いのちゃん"
              className="w-full h-full object-cover"
              style={{ objectPosition: '50% 8%', transform: 'scale(1.2)', transformOrigin: '50% 8%' }}
            />
          </div>
          <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-400 border-2 border-white shadow-sm"></span>
        </div>

        <div className="min-w-0 flex-1">
          {/* 名前 + オンライン/タイピング */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold leading-tight drop-shadow-sm whitespace-nowrap" style={{ color: 'rgba(30,30,30,0.9)' }}>
              いのちゃん
            </span>
            {isTyping ? (
              <span className="flex items-center gap-0.5">
                <span className="flex gap-0.5 items-center">
                  <span className="w-1 h-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1 h-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '120ms' }}></span>
                  <span className="w-1 h-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '240ms' }}></span>
                </span>
                <span className="text-[9px] font-semibold whitespace-nowrap" style={{ color: 'rgba(16,185,129,1)' }}>入力中…</span>
              </span>
            ) : (
              <span className="text-[9px] font-medium whitespace-nowrap" style={{ color: 'rgba(16,185,129,0.9)' }}>
                オンライン
              </span>
            )}
          </div>
          {/* レベルバッジ + メッセージ数（1行） */}
          <div className="flex items-center gap-1 mt-0.5 overflow-hidden">
            <span className="text-[9px] px-1 py-0.5 rounded-full bg-black/10 border border-black/10 font-semibold whitespace-nowrap flex-shrink-0" style={{ color: 'rgba(30,30,30,0.85)' }}>
              {levelEmoji} Lv.{intimacyLevel}
            </span>
            <span className="text-[9px] truncate font-medium" style={{ color: 'rgba(30,30,30,0.6)' }}>
              {levelLabel}
            </span>
            <span className="text-[9px] px-1 py-0.5 rounded-full bg-black/8 border border-black/10 font-medium whitespace-nowrap flex-shrink-0" style={{ color: 'rgba(30,30,30,0.65)' }}>
              💬{messagesUsedToday}/{messagesLimitPerDay}
            </span>
          </div>
        </div>
      </div>

      {/* 右：アクションボタン */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={() => navigate('/ranking')}
          className="px-1.5 py-1 flex items-center gap-0.5 bg-black/10 hover:bg-black/20 backdrop-blur-sm rounded-full transition-all duration-300 cursor-pointer border border-black/10 shadow-sm"
        >
          <span className="text-[11px]">🏆</span>
          <span className="text-[9px] font-bold whitespace-nowrap" style={{ color: 'rgba(30,30,30,0.85)' }}>
            ランキング
          </span>
        </button>

        <button
          onClick={() => navigate('/notifications', { state: { from: '/' } })}
          className="relative w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer backdrop-blur-sm hover:bg-black/10 border border-black/10 hover:border-black/20"
          style={{ color: 'rgba(30,30,30,0.8)' }}
        >
          <i className="ri-notification-3-line text-sm"></i>
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 min-w-[12px] h-3 bg-red-400 text-white text-[7px] font-bold rounded-full flex items-center justify-center px-0.5 shadow-sm">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        <button
          onClick={() => navigate('/memory')}
          className="w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer backdrop-blur-sm hover:bg-black/10 border border-black/10 hover:border-black/20"
          style={{ color: 'rgba(30,30,30,0.8)' }}
        >
          <i className="ri-settings-3-line text-sm"></i>
        </button>
      </div>
    </header>
  );
}
